// Типы и эндпоинты для массовых сообщений (админ)

export interface SendMassMessageRequest {
    message?: string;
    category: number;
    bot_keys: string[];
    zoom?: boolean;
    zoom_url?: string;
    zoom_code?: string;
    photo?: File;
    video?: File;
}

export interface SendMassMessageResponse {
    users_count: number;
    bots_count?: number;
    jobs_count?: number;
    batch_id: string;
}

export interface BatchStatusResponse {
    data: {
        progress: number;
        total: number;
        processed: number;
        failed: number;
        delivered?: number;
        finished?: boolean;
        cancelled?: boolean;
    };
}

export interface TelegramBotOption {
    key: string;
    name: string;
    is_default: boolean;
}

export interface TelegramBotsResponse {
    success: boolean;
    data: TelegramBotOption[];
}

export interface MessagingCampaign {
    id: number;
    title: string | null;
    status: string;
    category: number;
    bot_keys: string[];
    millennium_news_id?: number | null;
    millennium_external_id?: string | null;
    millennium_meta?: {
        category?: "news" | "update" | "personal";
        recipients?: string;
        titles?: Partial<Record<"ru" | "en" | "uk" | "ka", string>>;
    } | null;
    message: string | null;
    messages?: Partial<Record<"ru" | "en" | "uk" | "ka", string>> | null;
    zoom: boolean;
    zoom_url: string | null;
    zoom_code: string | null;
    photo_url: string | null;
    video_url: string | null;
    button_text: string | null;
    button_url: string | null;
    scheduled_at: string | null;
    sent_at: string | null;
    batch_id: string | null;
    users_count: number | null;
    bots_count: number | null;
    jobs_count: number | null;
    error: string | null;
    created_at: string | null;
}

export interface MessagingCampaignsResponse {
    success: boolean;
    data: MessagingCampaign[];
}

export interface MessagingCampaignMutationResponse {
    success: boolean;
    message?: string;
    batch_id?: string;
    users_count?: number;
    bots_count?: number;
    jobs_count?: number;
    campaign?: MessagingCampaign;
}

export interface SendMillenniumNewsRequest {
    title?: string;
    message: string;
    message_format?: "b64";
    millennium_category: "news" | "update" | "personal";
    millennium_recipients?: string;
    /** публічний URL уже завантаженого фото (без нового файла) */
    image_url?: string;
    /** стабільний ключ для WRP meta.hub_external_id (≤64) */
    external_id?: string;
}

export interface SendMillenniumNewsResponse {
    success: boolean;
    message?: string;
    millennium?: unknown;
    campaign?: MessagingCampaign;
    payload?: {
        category: string;
        audience_mode: string;
        user_ids?: number[] | null;
        image_url?: string | null;
        millennium_news_id?: number | null;
        external_id?: string;
        idempotent?: boolean;
        locales?: string[];
    };
}

export interface MillenniumStreamUrl {
    platform: "youtube" | "twitch" | "zoom" | "discord" | "telegram" | "other";
    url: string;
    label?: string;
}

export interface SendMillenniumBroadcastRequest {
    title?: string;
    starts_at: string;
    ends_at: string;
    stream_urls: MillenniumStreamUrl[];
}

export interface MillenniumBroadcastEditableFields {
    title?: boolean;
    starts_at?: boolean;
    ends_at?: boolean;
    stream_urls?: boolean;
    recording_url?: boolean;
}

export interface SendMillenniumBroadcastResponse {
    success: boolean;
    message?: string;
    millennium?: unknown;
    broadcast?: MillenniumBroadcastItem;
}

export interface MillenniumBroadcastItem {
    id: number;
    millennium_id: number | null;
    title: string | null;
    starts_at: string;
    ends_at: string;
    recording_url: string | null;
    stream_urls: MillenniumStreamUrl[];
    status: string | null;
    phase: "upcoming" | "live" | "ended" | string;
    editable_fields?: MillenniumBroadcastEditableFields | null;
    created_at: string | null;
}

export interface MillenniumBroadcastsResponse {
    success: boolean;
    data: MillenniumBroadcastItem[];
}

export interface MillenniumBroadcastDetailResponse {
    success: boolean;
    message?: string;
    broadcast?: MillenniumBroadcastItem;
    millennium?: unknown;
}

export const messagingEndpoints = {
    getTelegramBots: {
        query: () => "/admin/messaging/telegram-bots",
    },

    translateMessage: {
        query: (body: {
            html: string;
            from?: string;
            to: string;
            message_format?: string;
        }) => ({
            url: "/admin/messaging/translate",
            method: "POST",
            body,
        }),
    },

    sendMillenniumNews: {
        query: (body: SendMillenniumNewsRequest | FormData) => ({
            url: "/admin/messaging/millennium-news",
            method: "POST",
            body,
        }),
    },

    sendMillenniumBroadcast: {
        query: (body: SendMillenniumBroadcastRequest) => ({
            url: "/admin/messaging/millennium-broadcast",
            method: "POST",
            body,
        }),
    },

    listMillenniumBroadcasts: {
        query: (arg?: { phase?: string; refresh?: boolean } | void) => {
            const params = new URLSearchParams();
            if (arg && typeof arg === "object") {
                if (arg.phase && arg.phase !== "all") {
                    params.set("phase", arg.phase);
                }
                if (arg.refresh) {
                    params.set("refresh", "1");
                }
            }
            const q = params.toString();
            return q
                ? `/admin/messaging/millennium-broadcasts?${q}`
                : "/admin/messaging/millennium-broadcasts";
        },
    },

    showMillenniumBroadcast: {
        query: (id: number) => `/admin/messaging/millennium-broadcasts/${id}`,
    },

    endMillenniumBroadcast: {
        query: (id: number) => ({
            url: `/admin/messaging/millennium-broadcasts/${id}/end`,
            method: "POST",
        }),
    },

    startMillenniumBroadcast: {
        query: (id: number) => ({
            url: `/admin/messaging/millennium-broadcasts/${id}/start`,
            method: "POST",
        }),
    },

    patchMillenniumBroadcast: {
        query: ({
            id,
            ...body
        }: {
            id: number;
            recording_url?: string;
            title?: string;
            starts_at?: string;
            ends_at?: string;
            stream_urls?: MillenniumStreamUrl[];
        }) => ({
            url: `/admin/messaging/millennium-broadcasts/${id}`,
            method: "PATCH",
            body,
        }),
    },

    destroyMillenniumBroadcast: {
        query: (id: number) => ({
            url: `/admin/messaging/millennium-broadcasts/${id}`,
            method: "DELETE",
        }),
    },

    sendMassMessage: {
        query: (body: SendMassMessageRequest | FormData) => ({
            url: "/admin/messaging/send-mass-message",
            method: "POST",
            body,
        }),
    },

    getBatchStatus: {
        query: (batchId: string) => `/admin/messaging/batch-status/${batchId}`,
    },

    cancelBatch: {
        query: (batchId: string) => ({
            url: `/admin/messaging/cancel-batch/${batchId}`,
            method: "POST",
        }),
    },

    listCampaigns: {
        query: (status?: string) =>
            status
                ? `/admin/messaging/campaigns?status=${encodeURIComponent(status)}`
                : "/admin/messaging/campaigns",
    },

    storeCampaign: {
        query: (body: FormData) => ({
            url: "/admin/messaging/campaigns",
            method: "POST",
            body,
        }),
    },

    updateCampaign: {
        query: ({ id, body }: { id: number; body: FormData }) => ({
            url: `/admin/messaging/campaigns/${id}`,
            method: "POST",
            body,
        }),
    },

    cancelCampaign: {
        query: (id: number) => ({
            url: `/admin/messaging/campaigns/${id}/cancel`,
            method: "POST",
        }),
    },

    deleteCampaign: {
        query: (id: number) => ({
            url: `/admin/messaging/campaigns/${id}`,
            method: "DELETE",
        }),
    },

    clearHistory: {
        query: () => ({
            url: "/admin/messaging/campaigns/history",
            method: "DELETE",
        }),
    },
};

export const messagingTags = ["AdminMessaging", "MillenniumBroadcasts"] as const;
